import { useState, useEffect, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Slack } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import type { SupportChannelType } from '@/types/auth'

/** Ícone do WhatsApp — SVG branco preenchido. */
const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-7 h-7">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.857L.057 23.57a.75.75 0 0 0 .918.918l5.713-1.476A11.953 11.953 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.667-.5-5.2-1.378l-.373-.217-3.892 1.005 1.005-3.892-.217-.373A9.956 9.956 0 0 1 2 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z" />
  </svg>
)

/** Ícone do Microsoft Teams — quadrado branco com "T" na cor da marca. */
const TeamsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" fill="white" />
    <path d="M8 9h8M12 9v7" stroke="#6264A7" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
)

type ChannelMeta = {
  /** Cor da marca — fundo do botão. */
  brand: string
  /** Cor da marca no hover. */
  brandHover: string
  /** Título do card (a descrição vem do `label` da empresa). */
  defaultTitle: string
  icon: ReactNode
}

const CHANNELS: Record<SupportChannelType, ChannelMeta> = {
  whatsapp: {
    brand: '#25D366',
    brandHover: '#1ebe5d',
    defaultTitle: 'Comunidade AI Sym no WhatsApp',
    icon: <WhatsAppIcon />,
  },
  teams: {
    brand: '#6264A7',
    brandHover: '#54568f',
    defaultTitle: 'Canal de atendimento no Teams',
    icon: <TeamsIcon />,
  },
  slack: {
    brand: '#4A154B',
    brandHover: '#3a103b',
    defaultTitle: 'Canal de atendimento no Slack',
    icon: <Slack className="w-7 h-7 text-white" />,
  },
}

const DEFAULT_DESCRIPTION = 'Tire suas dúvidas com a comunidade.'

/**
 * Botão flutuante de canal de atendimento (canto inferior direito).
 * É por empresa: lê `user.supportChannel` do `/auth/me`. Sem canal
 * configurado — ou usuário deslogado — não renderiza nada.
 */
const SupportChannelButton = () => {
  const { user } = useAuth()
  const [bottom, setBottom] = useState(24)
  const [hovered, setHovered] = useState(false)

  // Sobe o botão quando o footer entra na viewport (não cobrir o rodapé).
  useEffect(() => {
    const footer = document.querySelector('footer')
    if (!footer) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottom(entry.isIntersecting ? 24 + entry.intersectionRect.height : 24)
      },
      { threshold: Array.from({ length: 101 }, (_, i) => i / 100) }
    )

    observer.observe(footer)
    return () => observer.disconnect()
  }, [])

  const channel = user?.supportChannel
  if (!channel) return null

  const meta = CHANNELS[channel.type]
  if (!meta) return null

  return (
    <div
      className="fixed right-6 z-50 flex items-end gap-3"
      style={{ bottom }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Card flutuante */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            className="bg-card border border-border rounded-2xl shadow-xl p-4 w-52 mb-4 text-center"
          >
            <div className="mb-2">
              <span className="text-sm font-semibold text-foreground">{meta.defaultTitle}</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {channel.label?.trim() || DEFAULT_DESCRIPTION}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Badge + Botão */}
      <div className="flex flex-col items-center gap-3">
        <span className="bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1 rounded-full shadow-md whitespace-nowrap">
          Ajuda
        </span>

        <a
          href={channel.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={meta.defaultTitle}
          className="relative w-14 h-14 flex items-center justify-center"
        >
          <span
            className="absolute inline-flex w-[85%] h-[85%] rounded-full opacity-30 animate-ping"
            style={{ backgroundColor: meta.brand }}
          />
          <span
            className="relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors duration-200"
            style={{ backgroundColor: hovered ? meta.brandHover : meta.brand }}
          >
            {meta.icon}
          </span>
        </a>
      </div>
    </div>
  )
}

export default SupportChannelButton
